import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  Inject,
  input,
  OnDestroy,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { type Icon, IconComponent } from '@examdojo/core/icon';
import { MathInputComponent } from './controls/math-input/math-input.component';
import { SliderComponent } from './controls/slider/slider.component';
import type { ActionDefinition } from './model/widget-action.model';
import type { BaseWidget } from './model/widget-base.model';
import type { WidgetConfig } from './model/widget-config.model';
import type {
  WidgetButtonControl,
  WidgetButtonGroupControl,
  WidgetControl,
  WidgetMathInputControl,
  WidgetSliderControl,
  WidgetSliderGroupControl,
} from './model/widget-control.model';
import { WIDGET_FACTORY, type WidgetFactory } from './model/widget-factory.model';
import { WidgetOrchestratorService } from './orchestration/widget-orchestrator.service';

// Configuration for a widget component instance.
// This extends WidgetConfig and adds Angular component-specific props.
export interface WidgetComponentConfig extends WidgetConfig {
  interactive?: boolean; // Show interactive wrapper with header
  title?: string; // Title for interactive header
}

/** A group of controls that should be rendered together in an input-group */
interface ControlGroup {
  type: 'slider' | 'math-input';
  controls: Array<WidgetSliderControl | WidgetMathInputControl>;
}

/** Either a single control or a group of controls */
type ControlOrGroup = WidgetControl | ControlGroup;

function isControlGroup(item: ControlOrGroup): item is ControlGroup {
  return 'controls' in item && Array.isArray(item.controls);
}

// Generic Angular component that works with any widget framework.
// Automatically selects the appropriate factory based on widget type.
@Component({
  selector: 'dojo-widget',
  standalone: true,
  imports: [NgTemplateOutlet, IconComponent, SliderComponent, MathInputComponent],
  templateUrl: './widget.component.html',
  styleUrls: ['./widget.component.scss'],
  host: { class: 'block h-full w-full' },
})
export class WidgetComponent implements AfterViewInit, OnDestroy {
  constructor(
    @Inject(WIDGET_FACTORY) private readonly factories: WidgetFactory[],
    private readonly orchestrator: WidgetOrchestratorService,
  ) {
    effect(() => {
      const config = this.config();
      if (this.isInitialized) {
        this.scheduleReinitialize(config);
      }
    });

    // Initialize control states from config
    effect(
      () => {
        const behavior = this.config().behavior;
        const controls = behavior?.controls ?? [];

        const sliderVals: Record<string, number> = {};
        const mathInputVals: Record<string, number> = {};
        const activeButtons: Record<string, string> = {};

        for (const control of controls) {
          if (control.type === 'slider') {
            sliderVals[control.id] = control.defaultValue ?? control.min;
          } else if (control.type === 'slider-group') {
            for (const slider of control.sliders) {
              sliderVals[slider.id] = slider.defaultValue ?? slider.min;
            }
          } else if (control.type === 'math-input') {
            mathInputVals[control.id] = control.defaultValue ?? 0;
          } else if (control.type === 'button-group' && control.buttons.length > 0) {
            activeButtons[control.id] = control.buttons[0].id;
          }
        }

        this.sliderValues.set(sliderVals);
        this.mathInputValues.set(mathInputVals);
        this.activeButtonInGroup.set(activeButtons);
      },
      { allowSignalWrites: true },
    );
  }

  readonly config = input.required<WidgetComponentConfig>();
  readonly widgetReady = output<BaseWidget>();

  /** Active button in each button group */
  readonly activeButtonInGroup = signal<Record<string, string>>({});

  /** Slider values */
  readonly sliderValues = signal<Record<string, number>>({});

  /** Math input values */
  readonly mathInputValues = signal<Record<string, number>>({});

  /** Controls filtered and grouped for top-bar position */
  readonly topBarControls = computed(() => this.getGroupedControlsByPosition('top-bar'));

  /** Controls filtered and grouped for bottom-bar position */
  readonly bottomBarControls = computed(() => this.getGroupedControlsByPosition('bottom-bar'));

  /** Controls filtered and grouped for canvas-overlay position */
  readonly canvasOverlayControls = computed(() => this.getGroupedControlsByPosition('canvas-overlay'));

  private readonly widgetContainer = viewChild.required<ElementRef<HTMLDivElement>>('widgetContainer');
  private widget: BaseWidget | null = null;
  private widgetId = `widget-${Math.random().toString(36).slice(2, 11)}`;
  private isInitialized = false;
  private isDestroyed = false;
  private reinitializationInProgress = false;
  private pendingConfig: WidgetComponentConfig | null = null;

  async ngAfterViewInit(): Promise<void> {
    await this.initializeWidget();
    this.isInitialized = true;
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.pendingConfig = null; // Cancel any pending reinitialization

    if (this.widget) {
      this.orchestrator.unregisterWidget(this.widgetId);
      this.widget.destroy();
      this.widget = null;
    }
  }

  /** Type guard for template - checks if item is a ControlGroup */
  isGroup(item: ControlOrGroup): item is ControlGroup {
    return isControlGroup(item);
  }

  /** Type assertion helper for icon */
  asIcon(icon: string): Icon {
    return icon as Icon;
  }

  /** Determine if button label should be shown */
  shouldShowLabel(control: { icon?: string; showLabel?: boolean; label: string }): boolean {
    if (control.showLabel !== undefined) {
      return control.showLabel;
    }
    return !control.icon;
  }

  /** Get slider value with fallback to default */
  getSliderValue(control: WidgetSliderControl): number {
    const values = this.sliderValues();
    return control.id in values ? values[control.id] : control.defaultValue ?? control.min;
  }

  /** Get slider value within a slider group */
  getSliderGroupValue(group: WidgetSliderGroupControl, sliderId: string): number {
    const values = this.sliderValues();
    if (sliderId in values) {
      return values[sliderId];
    }
    const slider = group.sliders.find((s) => s.id === sliderId);
    return slider?.defaultValue ?? slider?.min ?? 0;
  }

  /** Get math input value with fallback to default */
  getMathInputValue(control: WidgetMathInputControl): number {
    const values = this.mathInputValues();
    return control.id in values ? values[control.id] : control.defaultValue ?? 0;
  }

  async onButtonClick(control: WidgetButtonControl): Promise<void> {
    if (!this.widget) {
      return;
    }
    await this.widget.executeAction(control.trigger);
  }

  async onButtonGroupClick(
    group: WidgetButtonGroupControl,
    button: Omit<WidgetButtonControl, 'type' | 'position'>,
  ): Promise<void> {
    if (!this.widget) {
      return;
    }

    this.activeButtonInGroup.update((states) => ({ ...states, [group.id]: button.id }));
    await this.widget.executeAction(button.trigger);
  }

  async onSliderChange(control: WidgetSliderControl, value: number): Promise<void> {
    if (!this.widget) {
      return;
    }

    this.sliderValues.update((vals) => ({ ...vals, [control.id]: value }));

    const action: ActionDefinition = {
      ...control.trigger,
      payload: { ...control.trigger.payload, value },
    };
    await this.widget.executeAction(action);
  }

  async onSliderGroupChange(control: WidgetSliderGroupControl, event: { id: string; value: number }): Promise<void> {
    if (!this.widget) {
      return;
    }

    this.sliderValues.update((vals) => ({ ...vals, [event.id]: event.value }));

    const slider = control.sliders.find((s) => s.id === event.id);
    if (slider) {
      const action: ActionDefinition = {
        ...slider.trigger,
        payload: { ...slider.trigger.payload, value: event.value },
      };
      await this.widget.executeAction(action);
    }
  }

  async onMathInputChange(control: WidgetMathInputControl, value: number): Promise<void> {
    if (!this.widget) {
      return;
    }

    this.mathInputValues.update((vals) => ({ ...vals, [control.id]: value }));

    const action: ActionDefinition = {
      ...control.trigger,
      payload: { ...control.trigger.payload, value },
    };
    await this.widget.executeAction(action);
  }

  /**
   * Public API for direct widget control using the action system.
   */
  async dispatch(action: ActionDefinition): Promise<void> {
    if (!this.widget) {
      throw new Error('Widget not initialized');
    }
    await this.widget.executeAction(action);
  }

  /**
   * Get the underlying widget instance.
   */
  getWidget(): BaseWidget | null {
    return this.widget;
  }

  /**
   * Get the widget ID for use with orchestrator.
   */
  getWidgetId(): string {
    return this.widgetId;
  }

  /**
   * Get controls for a position, grouping adjacent sliders and math-inputs.
   * Adjacent controls of the same type (slider or math-input) are wrapped in a ControlGroup.
   */
  private getGroupedControlsByPosition(position: WidgetControl['position']): ControlOrGroup[] {
    const behavior = this.config().behavior;
    const controls = behavior?.controls ?? [];
    const filtered = controls.filter((c) => (c.position ?? 'top-bar') === position);

    const result: ControlOrGroup[] = [];
    let currentGroup: ControlGroup | null = null;

    for (const control of filtered) {
      const groupableType = control.type === 'slider' || control.type === 'math-input' ? control.type : null;

      if (groupableType) {
        if (currentGroup && currentGroup.type === groupableType) {
          // Add to existing group
          currentGroup.controls.push(control as WidgetSliderControl | WidgetMathInputControl);
        } else {
          // Start new group
          if (currentGroup) {
            result.push(currentGroup);
          }
          currentGroup = {
            type: groupableType,
            controls: [control as WidgetSliderControl | WidgetMathInputControl],
          };
        }
      } else {
        // Non-groupable control - flush current group and add control
        if (currentGroup) {
          result.push(currentGroup);
          currentGroup = null;
        }
        result.push(control);
      }
    }

    // Don't forget the last group
    if (currentGroup) {
      result.push(currentGroup);
    }

    return result;
  }

  private async initializeWidget(): Promise<void> {
    try {
      const widgetType = this.config().type;

      // Find factory that can handle this widget type
      const factory = this.factories.find((f) => f.has(widgetType));

      if (!factory) {
        throw new Error(
          `No factory found for widget type: '${widgetType}'. Available types: ${this.factories
            .flatMap((f) => f.getRegisteredTypes())
            .join(', ')}`,
        );
      }

      // Load framework instance
      const frameworkInstance = await factory.loadFramework();

      // Check if component was destroyed during async operation
      if (this.isDestroyed) {
        return;
      }

      // Create widget
      const container = this.widgetContainer().nativeElement;
      this.widget = factory.create(widgetType, container, frameworkInstance);

      // Initialize widget with the full config (excluding Angular-specific props)
      const { interactive: _interactive, title: _title, ...widgetConfig } = this.config();
      this.widget.init(widgetConfig as WidgetConfig);

      // Register with orchestrator
      this.orchestrator.registerWidget(this.widgetId, this.widget);
      this.widgetReady.emit(this.widget);
    } catch (error) {
      console.error('Failed to initialize widget:', error);
    }
  }

  private scheduleReinitialize(config: WidgetComponentConfig): void {
    if (this.isDestroyed) {
      return;
    }

    if (this.reinitializationInProgress) {
      this.pendingConfig = config;
      return;
    }

    this.performReinitialize(config);
  }

  private async performReinitialize(_config: WidgetComponentConfig): Promise<void> {
    this.reinitializationInProgress = true;

    try {
      if (this.widget) {
        this.orchestrator.unregisterWidget(this.widgetId);
        this.widget.destroy();
        this.widget = null;
      }

      // Abort if component was destroyed during cleanup
      if (this.isDestroyed) {
        return;
      }

      this.widgetId = `widget-${Math.random().toString(36).slice(2, 11)}`;
      await this.initializeWidget();
    } finally {
      this.reinitializationInProgress = false;

      // Only process pending config if component is still alive
      if (this.pendingConfig && !this.isDestroyed) {
        const nextConfig = this.pendingConfig;
        this.pendingConfig = null;
        this.scheduleReinitialize(nextConfig);
      }
    }
  }
}
